import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axiosInstance from '../../helpers/api';
import '../../styles/offernowaz-enhanced.css';

// Form validation schema
const leadFormSchema = z.object({
  propertyAddress: z.string().min(5, 'Please enter a valid property address'),
  city: z.string().min(2, 'Please enter a city'),
  zip: z.string().regex(/^\d{5}$/, 'Please enter a valid 5-digit ZIP code'),
  propertyType: z.enum(['single-family', 'condo', 'townhome', 'multi-family']),
  occupancy: z.enum(['owner', 'tenant', 'vacant']),
  condition: z.enum(['light', 'moderate', 'heavy']),
  timeline: z.enum(['asap', '30days', '60plus']),
  reasonForSelling: z.string().optional(),
  name: z.string().min(2, 'Please enter your name'),
  phone: z.string().regex(/^[\d\s\-\(\)]+$/, 'Please enter a valid phone number').min(10, 'Phone number must be at least 10 digits'),
  email: z.string().email('Please enter a valid email').optional().or(z.literal('')),
  consent: z.boolean().refine((val) => val === true, {
    message: 'You must agree to be contacted'
  }),
  website: z.string().optional() // Honeypot field
});

type LeadFormData = z.infer<typeof leadFormSchema>;

const OfferNowAZHome: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Set page title
  React.useEffect(() => {
    document.title = 'OfferNow Arizona - We Buy Houses Fast | Cash Offers in 24 Hours';
    
    // Cleanup - restore original title when component unmounts
    return () => {
      document.title = 'Kairos IQ';
    };
  }, []);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadFormData>({
    resolver: zodResolver(leadFormSchema),
  });

  const onSubmit = async (data: LeadFormData) => {
    // Spam check - if honeypot field is filled, silently reject
    if (data.website) {
      window.location.href = '/offernowaz/thank-you';
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axiosInstance.post('/offernowaz/submit-lead', {
        ...data,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        source: 'offernowaz.com'
      });
      
      if (response.data.success) {
        // Track conversion event
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
            'value': 1.0,
            'currency': 'USD'
          });
        }
        
        // Redirect to thank you page
        window.location.href = '/offernowaz/thank-you';
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offernowaz-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-grid">
            {/* Left Column - Content */}
            <div className="hero-content">
              <h1 className="hero-title">
                Sell Your Arizona Property <span style={{color: '#68d391'}}>As-Is</span> — Get an Offer in <span style={{color: '#68d391'}}>24 Hours</span>
              </h1>
              <p className="hero-subtitle">
                No repairs. No showings. Flexible closing. Tell us about the property and we'll follow up.
              </p>
              
              {/* Mobile form shows here */}
              <div className="mobile-form-container">
                <LeadForm 
                  register={register}
                  errors={errors}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                  isSubmitting={isSubmitting}
                />
              </div>
              
              {/* Benefits list */}
              <div className="benefits-grid">
                <div className="benefit-item">
                  <svg className="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Fair Cash Offers</span>
                </div>
                <div className="benefit-item">
                  <svg className="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Close in 7-30 Days</span>
                </div>
                <div className="benefit-item">
                  <svg className="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>No Agent Fees</span>
                </div>
                <div className="benefit-item">
                  <svg className="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Sell As-Is</span>
                </div>
              </div>
            </div>
            
            {/* Right Column - Form (Desktop) */}
            <div className="desktop-form-container">
              <LeadForm 
                register={register}
                errors={errors}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <TrustStrip />

      {/* How It Works */}
      <HowItWorks />

      {/* What We Buy */}
      <WhatWeBuy />

      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* FAQ */}
      <FAQ />

      {/* Footer */}
      <OfferNowFooter />
    </div>
  );
};

// Lead Form Component
interface LeadFormProps {
  register: any;
  errors: any;
  handleSubmit: any;
  onSubmit: any;
  isSubmitting: boolean;
}

const LeadForm: React.FC<LeadFormProps> = ({ register, errors, handleSubmit, onSubmit, isSubmitting }) => {
  return (
    <div className="lead-form-card">
      <h3 className="form-title">Get Your Cash Offer</h3>
      
      <form onSubmit={handleSubmit(onSubmit)} className="lead-form">
        {/* Property Address */}
        <div className="form-group">
          <label>Property Address*</label>
          <input
            type="text"
            {...register('propertyAddress')}
            className="form-control"
            placeholder="123 Main St"
          />
          {errors.propertyAddress && (
            <p className="error-message">{errors.propertyAddress.message}</p>
          )}
        </div>
        
        {/* City and ZIP */}
        <div className="form-row">
          <div className="form-group">
            <label>City*</label>
            <input
              type="text"
              {...register('city')}
              className="form-control"
              placeholder="Phoenix"
            />
            {errors.city && (
              <p className="error-message">{errors.city.message}</p>
            )}
          </div>
          
          <div className="form-group">
            <label>ZIP Code*</label>
            <input
              type="text"
              {...register('zip')}
              maxLength={5}
              className="form-control"
              placeholder="85001"
            />
            {errors.zip && (
              <p className="error-message">{errors.zip.message}</p>
            )}
          </div>
        </div>
        
        {/* Property Type */}
        <div className="form-group">
          <label>Property Type*</label>
          <select {...register('propertyType')} className="form-control">
            <option value="">Select...</option>
            <option value="single-family">Single Family Home</option>
            <option value="condo">Condo</option>
            <option value="townhome">Townhome</option>
            <option value="multi-family">Multi-Family</option>
          </select>
          {errors.propertyType && (
            <p className="error-message">{errors.propertyType.message}</p>
          )}
        </div>
        
        {/* Occupancy and Condition */}
        <div className="form-row">
          <div className="form-group">
            <label>Occupancy*</label>
            <select {...register('occupancy')} className="form-control">
              <option value="">Select...</option>
              <option value="owner">Owner Occupied</option>
              <option value="tenant">Tenant Occupied</option>
              <option value="vacant">Vacant</option>
            </select>
            {errors.occupancy && (
              <p className="error-message">{errors.occupancy.message}</p>
            )}
          </div>
          
          <div className="form-group">
            <label>Condition*</label>
            <select {...register('condition')} className="form-control">
              <option value="">Select...</option>
              <option value="light">Light Repairs Needed</option>
              <option value="moderate">Moderate Repairs Needed</option>
              <option value="heavy">Heavy Repairs Needed</option>
            </select>
            {errors.condition && (
              <p className="error-message">{errors.condition.message}</p>
            )}
          </div>
        </div>
        
        {/* Timeline */}
        <div className="form-group">
          <label>When do you need to sell?*</label>
          <select {...register('timeline')} className="form-control">
            <option value="">Select...</option>
            <option value="asap">ASAP</option>
            <option value="30days">Within 30 days</option>
            <option value="60plus">60+ days</option>
          </select>
          {errors.timeline && (
            <p className="error-message">{errors.timeline.message}</p>
          )}
        </div>
        
        {/* Contact Info */}
        <div className="form-group">
          <label>Your Name*</label>
          <input
            type="text"
            {...register('name')}
            className="form-control"
            placeholder="John Doe"
          />
          {errors.name && (
            <p className="error-message">{errors.name.message}</p>
          )}
        </div>
        
        <div className="form-group">
          <label>Phone Number*</label>
          <input
            type="tel"
            {...register('phone')}
            className="form-control"
            placeholder="(555) 123-4567"
          />
          {errors.phone && (
            <p className="error-message">{errors.phone.message}</p>
          )}
        </div>
        
        <div className="form-group">
          <label>Email (optional)</label>
          <input
            type="email"
            {...register('email')}
            className="form-control"
            placeholder="john@example.com"
          />
          {errors.email && (
            <p className="error-message">{errors.email.message}</p>
          )}
        </div>
        
        {/* Honeypot field - hidden from users */}
        <div style={{ display: 'none' }}>
          <input
            type="text"
            {...register('website')}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        
        {/* Consent */}
        <div className="consent-group">
          <input
            type="checkbox"
            {...register('consent')}
            id="consent"
            className="consent-checkbox"
          />
          <label htmlFor="consent" className="consent-label">
            I agree to be contacted by phone/text/email regarding my request.*
          </label>
        </div>
        {errors.consent && (
          <p className="error-message">{errors.consent.message}</p>
        )}
        
        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="submit-button"
        >
          {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
        </button>
        
        <p className="disclaimer-text">
          Offers are subject to property evaluation.
        </p>
      </form>
    </div>
  );
};

// Trust Strip Component
const TrustStrip: React.FC = () => {
  const trustPoints = [
    { icon: '📍', text: 'Local Arizona team' },
    { icon: '🏠', text: 'As-is purchases' },
    { icon: '💰', text: 'No commissions' },
    { icon: '📅', text: 'Flexible close date' }
  ];

  return (
    <section className="trust-strip">
      <div className="container">
        <div className="trust-items">
          {trustPoints.map((point, index) => (
            <div key={index} className="trust-item">
              <span className="trust-icon">{point.icon}</span>
              <span className="trust-text">{point.text}</span>
            </div>
          ))}
        </div>
        <p className="trust-disclaimer">
          *Offers are subject to property evaluation.
        </p>
      </div>
    </section>
  );
};

// How It Works Component
const HowItWorks: React.FC = () => {
  const steps = [
    {
      number: "1",
      title: "Submit Property Details",
      description: "Fill out our simple form with your property information. It takes less than 2 minutes.",
      icon: '📝'
    },
    {
      number: "2", 
      title: "We Review Market Data + Condition",
      description: "Our team analyzes recent sales, property condition, and market trends to prepare a fair offer.",
      icon: '📊'
    },
    {
      number: "3",
      title: "We Call/Text Within 24 Hours",
      description: "You'll receive a no-obligation cash offer. Accept it, and we can close in as little as 7 days.",
      icon: '📞'
    }
  ];

  const handleGetStarted = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.querySelector('.lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="how-it-works">
      <div className="container">
        <h2 className="section-title">How It Works</h2>
        
        <div className="steps-grid">
          {steps.map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-icon">{step.icon}</div>
              <div className="step-number">{step.number}</div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
        
        <div className="cta-container">
          <a href="#get-offer" onClick={handleGetStarted} className="cta-button">
            Get Started Now
          </a>
        </div>
      </div>
    </section>
  );
};

// What We Buy Component
const WhatWeBuy: React.FC = () => {
  const propertyTypes = [
    { title: "Fixer Uppers", description: "Properties needing repairs or updates", icon: "🔨" },
    { title: "Inherited Properties", description: "Estate sales and probate situations", icon: "🏠" },
    { title: "Tenant-Occupied", description: "Rental properties with tenants", icon: "🔑" },
    { title: "Behind on Payments", description: "Facing foreclosure or financial hardship", icon: "⏰" },
    { title: "Code Violations", description: "Properties with city code issues", icon: "📋" },
    { title: "Any Condition", description: "From move-in ready to major repairs", icon: "✓" }
  ];

  return (
    <section className="what-we-buy">
      <div className="container">
        <h2 className="section-title">What We Buy</h2>
        <p className="section-subtitle">
          We purchase all types of properties in any condition. No situation is too complicated.
        </p>
        
        <div className="property-types-grid">
          {propertyTypes.map((type, index) => (
            <div key={index} className="property-type-card">
              <div className="property-type-icon">{type.icon}</div>
              <h3 className="property-type-title">{type.title}</h3>
              <p className="property-type-description">{type.description}</p>
            </div>
          ))}
        </div>
        
        <div className="professional-note">
          <p>
            <strong>Professional & Respectful:</strong> We understand that selling a property can be stressful. 
            Our team approaches every situation with empathy and professionalism, providing solutions that work for you.
          </p>
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Component
const WhyChooseUs: React.FC = () => {
  const benefits = [
    {
      title: "Sell As-Is",
      description: "No repairs, cleaning, or staging required. We buy properties in any condition.",
      icon: "🏗️"
    },
    {
      title: "Avoid Showings",
      description: "No open houses, no strangers walking through your home. One visit from us is all it takes.",
      icon: "🚫"
    },
    {
      title: "Flexible Close Date",
      description: "Close in as little as 7 days or take up to 60 days. You choose the timeline that works for you.",
      icon: "📅"
    },
    {
      title: "Simple Process",
      description: "No complicated paperwork or lengthy negotiations. We make selling your property easy.",
      icon: "✅"
    },
    {
      title: "Privacy Respected",
      description: "Your situation stays private. No signs in the yard, no MLS listings, no public exposure.",
      icon: "🔒"
    },
    {
      title: "No Fees or Commissions", 
      description: "Unlike traditional sales, you pay no agent commissions, closing costs, or hidden fees.",
      icon: "💵"
    }
  ];

  return (
    <section className="why-choose-us">
      <div className="container">
        <h2 className="section-title">Why Sellers Choose Us</h2>
        
        <div className="benefits-grid">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-icon-large">{benefit.icon}</div>
              <h3 className="benefit-title">{benefit.title}</h3>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
        
        <div className="testimonial">
          <p>
            We've helped hundreds of Arizona property owners sell quickly and easily. 
            Let us show you a better way to sell.
          </p>
        </div>
      </div>
    </section>
  );
};

// FAQ Component
const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const faqs = [
    {
      question: "How do you determine the offer?",
      answer: "We analyze recent comparable sales in your area, consider the property's condition, and factor in current market trends. Our goal is to provide a fair offer that reflects the true value of your property in its current state."
    },
    {
      question: "Do I need to clean or repair anything?",
      answer: "No! We buy properties as-is. You don't need to clean, repair, or even remove personal belongings. We handle everything after purchase."
    },
    {
      question: "What types of properties do you buy?",
      answer: "We purchase single-family homes, condos, townhomes, and multi-family properties in any condition throughout Arizona, with a focus on Maricopa County."
    },
    {
      question: "Do you charge fees or commissions?",
      answer: "No. Unlike traditional real estate sales, we don't charge any fees, commissions, or closing costs. The offer we make is the amount you receive."
    },
    {
      question: "How fast can you close?",
      answer: "We can close in as little as 7 days if needed. However, we're flexible and can work with your timeline, whether you need to close quickly or prefer to take up to 60 days."
    },
    {
      question: "What if the property has tenants?",
      answer: "We regularly purchase tenant-occupied properties. We'll work with you to ensure a smooth transition that respects existing lease agreements."
    },
    {
      question: "What if I'm behind on payments?",
      answer: "We understand financial hardship and can help. We've worked with many homeowners facing foreclosure or who are behind on payments. Time is critical in these situations, so reach out as soon as possible."
    },
    {
      question: "Is there any obligation after I submit?",
      answer: "No. Submitting your property information is free and comes with no obligation. You can review our offer and decide if it works for you. There's no pressure to accept."
    },
    {
      question: "Do you buy inherited/probate properties?",
      answer: "Yes. We have experience with probate and estate sales. We can help navigate the process and work with attorneys or estate representatives as needed."
    },
    {
      question: "What areas do you cover?",
      answer: "We purchase properties throughout Arizona, with primary focus on Maricopa County including Phoenix, Scottsdale, Mesa, Chandler, Gilbert, Tempe, Glendale, and surrounding areas."
    }
  ];
  
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  return (
    <section className="faq-section">
      <div className="container">
        <h2 className="section-title">Frequently Asked Questions</h2>
        
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
              >
                <span>{faq.question}</span>
                <svg
                  className={`faq-arrow ${openIndex === index ? 'rotate' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {openIndex === index && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="faq-cta">
          <p>Have more questions? We're here to help!</p>
          <a href="tel:+14805551234" className="phone-button">
            Call Us: (480) 555-1234
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const OfferNowFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="offernowaz-footer">
      <div className="container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-section">
            <h3>OfferNow Arizona LLC</h3>
            <p>
              We buy houses in any condition throughout Arizona.
            </p>
            <p>
              <strong>Service Area:</strong> Maricopa County and surrounding areas
            </p>
          </div>
          
          {/* Contact Info */}
          <div className="footer-section">
            <h4>Contact Us</h4>
            <p>
              <a href="tel:+14805551234">Phone: (480) 555-1234</a>
            </p>
            <p>
              <a href="mailto:info@offernowaz.com">Email: info@offernowaz.com</a>
            </p>
            <p>
              Hours: Mon-Fri 8AM-6PM, Sat 9AM-4PM
            </p>
          </div>
          
          {/* Legal Links */}
          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li>
                <a href="/offernowaz/privacy">Privacy Policy</a>
              </li>
              <li>
                <a href="/offernowaz/terms">Terms of Service</a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Disclaimer */}
        <div className="footer-disclaimer">
          <p>
            <strong>Disclaimer:</strong> OfferNow Arizona LLC is a real estate investment company. 
            Information provided is for general purposes and is not legal advice. All offers subject to property evaluation.
          </p>
          <p className="copyright">
            © {currentYear} OfferNow Arizona LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default OfferNowAZHome;